/* eslint-disable */
import type { GlobalAddress } from "../common/location.pb";

export enum IdentityVerificationDecision {
  /** VERIFIED - The provided identity information was successfully verified. */
  VERIFIED = "VERIFIED",
  /** FAILED_TO_VERIFY - The identity provided did not verify. */
  FAILED_TO_VERIFY = "FAILED_TO_VERIFY",
  /** UNAVAILABLE - We could not connect to the identity verification providers at this time. */
  UNAVAILABLE = "UNAVAILABLE",
  /**
   * RESCINDED - This decision was previously verified, but a change in policy or manual review forced us to
   * reverse the decision. This includes selfie reviews and old verifications that don't meet our
   * current strict matching policy.
   */
  RESCINDED = "RESCINDED",
  /**
   * UNDER_AGE - The identity provided was successfully verified, but the person is under 18 years old.
   * Not used for new verifications because, if under 18 years old, IDV is now blocked
   * before sent to providers for a decision.
   */
  UNDER_AGE = "UNDER_AGE",
  /** DECEASED - The identity provided was successfully verified, but the person is deceased. */
  DECEASED = "DECEASED",
  /**
   * INVALID_SSN - The provided full SSN is invalid or denylisted. This decision is made by us on the IDV Attempt
   * level and overrides any other decisions from external IDV providers such as IDOLOGY.
   */
  INVALID_SSN = "INVALID_SSN",
}

/**
 * A signal, based on the match results of an identity verification, indicating if the submitted
 * SSN belongs to the customer.
 */
export enum SsnSignal {
  /** POSITIVE - A positive signal that the SSN belongs to the customer (i.e. matching name and SSN). */
  POSITIVE = "POSITIVE",
  /** NEUTRAL - A neutral signal that the SSN belongs to the customer (i.e. SSN not available). */
  NEUTRAL = "NEUTRAL",
  /**
   * NEGATIVE - A negative signal that the SSN belongs to the customer (i.e. name and birth date match, and
   * SSN is available but does not match).
   */
  NEGATIVE = "NEGATIVE",
}

export interface Identity {
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  name_suffix?: string;
  birth_day?: number;
  birth_month?: number;
  birth_year?: number;
  ssn_last_four?: string;
  ssn_token?: string;
  ssn_valid?: boolean;
  zip?: string;
  address?: GlobalAddress;
  phone_number?: string;
  email?: string;
  ip_address?: string;
}

export interface IdentityVerificationAttempt {
  /**
   * In favour of attempt_token.
   *
   * @deprecated
   */
  id?: string;
  attempt_token?: string;
  customer_token?: string;
  created_at?: number;
  updated_at?: number;
  /** The identity submitted by the customer. */
  submission?: Identity;
  /** The decision for this attempt, potentially on the basis of multiple providers. */
  decision?: IdentityVerificationDecision;
  /**
   * A signal indicating if the submitted SSN belongs to the customer based on the best
   * verification for this attempt.
   */
  ssn_signal?: SsnSignal;
  /**
   * Results from calls to identity verification providers specifically for this attempt,
   * and all previous results for the same Identity.
   */
  verification_results?: IdentityVerificationResult[];
  invalidated_at?: number;
  rescinded_at?: number;
  vaulted_at?: number;
  valid?: boolean;
}

export interface CustomerIdvChangedEvent {
  /**
   * In favour of attempt_token.
   *
   * @deprecated
   */
  attempt_id?: string;
  customer_token?: string;
  created_at?: number;
  updated_at?: number;
  decision?: IdentityVerificationDecision;
  valid?: boolean;
  attempt_token?: string;
}

export interface IdentityVerificationResult {
  /**
   * In favour of verification_token.
   *
   * @deprecated
   */
  id?: string;
  verification_token?: string;
  created_at?: number;
  updated_at?: number;
  /** The name of the identity verification service being used. */
  provider?: string;
  /** The name of the IdMatch vendor used. (If the provider is IdMatch). */
  vendor?: string;
  /** The identity found by the provider. */
  match?: Identity;
  legal_name_result?: IdentityVerificationResultMatchResult;
  birth_date_result?: IdentityVerificationResultMatchResult;
  ssn_result?: IdentityVerificationResultMatchResult;
  address_result?: IdentityVerificationResultMatchResult;
  /** Decision on the basis of this provider's result. */
  decision?: IdentityVerificationDecision;
  /** The response from the provider as JSON. */
  response?: string;
  /** A signal indicating if the submitted SSN belongs to the customer. */
  ssn_signal?: SsnSignal;
  /** The response id from the idv provider. */
  provider_id?: string;
  /** The team that denylisted the customer (if applicable) */
  denylist_team?: IdentityVerificationResultDenylistTeam;
  /** The ms since Epoch at which ML overrode the decision for this verification. */
  ml_overrode_decision_at_millis?: number;
}

export enum IdentityVerificationResultMatchResult {
  NOT_MATCHED = "NOT_MATCHED",
  PARTIALLY_MATCHED = "PARTIALLY_MATCHED",
  MATCHED = "MATCHED",
  NOT_AVAILABLE = "NOT_AVAILABLE",
  NOT_VALID = "NOT_VALID",
  /**
   * NOT_APPLICABLE - A NOT_APPLICABLE match result means that field is not taken into account when determining
   * the identity verification decision.
   */
  NOT_APPLICABLE = "NOT_APPLICABLE",
  /** @deprecated */
  MATCHED_BUT_DENYLISTED = "MATCHED_BUT_DENYLISTED",
  MATCHED_BUT_FLAGGED_BY_RISK = "MATCHED_BUT_FLAGGED_BY_RISK",
  NOT_MATCHED_AND_FLAGGED_BY_RISK = "NOT_MATCHED_AND_FLAGGED_BY_RISK",
  MATCHED_BUT_DENYLISTED_BY_CMPL = "MATCHED_BUT_DENYLISTED_BY_CMPL",
  MATCHED_BUT_DENYLISTED_BY_RISK = "MATCHED_BUT_DENYLISTED_BY_RISK",
  PARTIALLY_MATCHED_BUT_FLAGGED_BY_RISK = "PARTIALLY_MATCHED_BUT_FLAGGED_BY_RISK",
  NOT_AVAILABLE_BUT_FLAGGED_BY_RISK = "NOT_AVAILABLE_BUT_FLAGGED_BY_RISK",
  NOT_VALID_BUT_FLAGGED_BY_RISK = "NOT_VALID_BUT_FLAGGED_BY_RISK",
  NOT_APPLICABLE_BUT_FLAGGED_BY_RISK = "NOT_APPLICABLE_BUT_FLAGGED_BY_RISK",
  NOT_MATCHED_DENYLISTED_BY_RISK = "NOT_MATCHED_DENYLISTED_BY_RISK",
  NOT_MATCHED_DENYLISTED_BY_CMPL = "NOT_MATCHED_DENYLISTED_BY_CMPL",
  MATCHED_BUT_DENYLISTED_BY_PROD = "MATCHED_BUT_DENYLISTED_BY_PROD",
  /**
   * NOT_MATCHED_DENYLISTED_BY_PROD - This has been copied to the cash-idv verification decision proto.
   * Please make any changes in both places.
   */
  NOT_MATCHED_DENYLISTED_BY_PROD = "NOT_MATCHED_DENYLISTED_BY_PROD",
}

export enum IdentityVerificationResultDenylistTeam {
  DENYLIST_TEAM_NOT_APPLICABLE = "DENYLIST_TEAM_NOT_APPLICABLE",
  COMPLIANCE = "COMPLIANCE",
  RISK = "RISK",
  PRODUCT = "PRODUCT",
}

/** Encapsulates the PII data for an IDV attempt. This gets encrypted and persisted in the DB. */
export interface IdvAttemptPiiData {
  legal_name?: string;
  /** Date of birth as yyyy/mm/dd. */
  birth_date?: string;
  ssn_last_four?: string;
  phone_number?: string;
  address?: GlobalAddress;
  is_vaulted?: boolean;
}

/** Encapsulates PII data for an IDV verification. This gets encrypted and persisted in the DB. */
export interface IdvVerificationPiiData {
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  birth_day?: number;
  birth_month?: number;
  birth_year?: number;
  ssn_last_four?: string;
  address?: GlobalAddress;
  response?: string;
  is_vaulted?: boolean;
}

/** Encapsulates PII data for an IDV state record. This gets encrypted and persisted in the DB. */
export interface IdvStatePiiData {
  verified_full_name?: string;
}

/** Encapsulates the PII data for demographic_collection */
export interface DemographicsCollectionIdvInfo {
  principalFirstName?: string;
  principalMiddleName?: string;
  principalLastName?: string;
  principalDob?: string;
  principalSsn?: string;
}
